import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  type IAppointment,
  type IReleaseDepositHoldRequest,
} from '@principle-theorem/principle-core/interfaces';
import {
  httpsCallable,
  isFailedPreconditionError,
  serialise,
  snapshot,
  type SerialisedData,
  type WithRef,
} from '@principle-theorem/shared';

/**
 * Break-glass control for a cancellation-insurance hold that the automatic
 * release did not clear. The server re-checks eligibility, so this only has to
 * report the outcome; the server derives the caller's identity.
 */
@Injectable({ providedIn: 'root' })
export class DepositHoldReleaseService {
  constructor(private _snackbar: MatSnackBar) {}

  /**
   * A failed-precondition rejection, such as a disconnected Stripe account,
   * carries a message staff can act on; anything else is worth a retry.
   */
  private static _failureMessage(error: unknown): string {
    if (isFailedPreconditionError(error)) {
      return error.message;
    }
    return 'Could not release the card hold. Please try again or contact support.';
  }

  async releaseHold(appointment: WithRef<IAppointment>): Promise<void> {
    this._snackbar.open('Releasing card hold...');
    try {
      await snapshot(
        httpsCallable<SerialisedData<IReleaseDepositHoldRequest>, void>(
          'http-stripe-releaseDepositHold'
        )(serialise({ appointmentRef: appointment.ref }))
      );
      this._snackbar.open('Card hold released');
    } catch (error) {
      this._snackbar.open(DepositHoldReleaseService._failureMessage(error));
      throw error;
    }
  }
}
